/**
 * SeatGlyph — tiny code-native pixel avatar for a battle seat (no external
 * image assets or runtime drawing, same technique as CardGlyph): a green
 * space-invader for the viewer, a red skull for the opponent. Colors follow the
 * seat accents so the avatar matches its PlayerPanel border.
 */
import type { ReactElement } from 'react';
import { COLORS } from './kit/tokens.js';

export type Seat = 'viewer' | 'opponent';

interface PixelGlyph {
  readonly rows: readonly string[];
  readonly colors: readonly string[];
}

/** Dark ink used for eyes/teeth on top of the seat's accent color. */
const INK = COLORS.fieldBg;

const SEAT_GLYPHS: Record<Seat, PixelGlyph> = {
  viewer: {
    colors: [COLORS.textOnDark, INK],
    rows: [
      '..1.....1..',
      '...1...1...',
      '..1111111..',
      '.111212111.',
      '11111111111',
      '1.1111111.1',
      '1.1.....1.1',
      '...11.11...',
    ],
  },
  opponent: {
    colors: [COLORS.rival, INK],
    rows: [
      '..11111..',
      '.1111111.',
      '111111111',
      '122111221',
      '122111221',
      '111111111',
      '111212111',
      '1.1.1.1.1',
    ],
  },
};

export interface SeatGlyphProps {
  seat: Seat;
  /** Explicit px size; omit to let CSS size the SVG (fills .cards-player-avatar). */
  size?: number;
}

export function SeatGlyph({ seat, size }: SeatGlyphProps): ReactElement {
  const glyph = SEAT_GLYPHS[seat];
  const width = glyph.rows[0]?.length ?? 0;
  const height = glyph.rows.length;
  return (
    <svg
      data-testid={`seat-glyph-${seat}`}
      viewBox={`0 0 ${width} ${height}`}
      {...(size === undefined ? {} : { width: size, height: size })}
      aria-hidden="true"
      focusable="false"
      shapeRendering="crispEdges"
      className="cards-seat-glyph"
    >
      {glyph.rows.flatMap((row, y) => [...row].map((pixel, x) => {
        if (pixel === '.') return null;
        const fill = glyph.colors[Number(pixel) - 1];
        return fill === undefined ? null : (
          <rect key={`${x}:${y}`} x={x} y={y} width="1" height="1" fill={fill} />
        );
      }))}
    </svg>
  );
}
